import { useCallback, useState } from 'react';

import { buildBasePath } from '@atlassian/post-office-context/build-base-path';
import type { PostOfficeEnvironmentValues } from '@atlassian/post-office-context/post-office-environment/types';
import type { PostOfficeContextValue } from '@atlassian/post-office-context/types';
import {
	defaultFetcher,
	requestInitialData,
} from '@atlassian/post-office-placement-shared/initial-data-client';
import {
	ATLASSIAN_CONSENT_COOKIE_KEY,
	ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY,
} from '@atlassian/browser-storage-controls/constants';
import { getPreloaderFnContext } from '@confluence/query-preloader-tools/entry-points/preloaderFnContext.ts';
import {
	slowQueryGuard,
	SLOW_QUERY_DEFAULT_TIMEOUT,
} from '@confluence/query-preloader-tools/entry-points/slowQueryGuard.ts';
import { StatsigConfigurations } from '@confluence/statsig-configurations/entry-points/StatsigConfigurations.ts';
import { getSSRRenderInputs } from '@confluence/ssr-utilities/entry-points/ssrRenderInputs.ts';
import { isLocal } from '@confluence/ssr-utilities/entry-points/env.ts';
import { cfetch } from '@confluence/network';
import { expValEquals } from '@confluence/feature-experiments';

import {
	type ConfluenceGlobalThisWithPostOfficeCache,
	PostOfficeCacheClient,
	type PostOfficeCache,
} from './post-office-cache-client.ts';
import {
	sessionToPostOfficeContext,
	transformConfluenceEnvToPostOfficeEnvironment,
} from './session-to-post-office-context.ts';

/**
 * Post Office class for managing in memory cache of Placement queries.
 * We store the cache reference on the global object to persist across SSR requests.
 */
const POST_OFFICE_QUERY_CACHE: PostOfficeCache =
	(globalThis as ConfluenceGlobalThisWithPostOfficeCache)?.__POST_OFFICE_QUERY_CACHE__ || {};

const postOfficeClient = new PostOfficeCacheClient(POST_OFFICE_QUERY_CACHE);

type OptionalEnvConfigsForPostOfficeEnvironment = Omit<PostOfficeEnvironmentValues, 'envConfigs'> &
	Partial<Pick<PostOfficeEnvironmentValues, 'envConfigs'>>;

export const resStatusAsString = ({ status, statusText }: Response): string =>
	JSON.stringify({ status, statusText });

export const assertResponseIsOk = (res: Response | undefined): Response => {
	if (typeof res === 'undefined') {
		throw new Error('No response from server');
	}

	if (res.ok) {
		return res;
	}

	throw new Error(resStatusAsString(res));
};

const wrapResolvedDataAsFetchResponse = (data: any) => {
	const init = {
		status: 200,
		statusText: 'ok!',
	};
	return new Response(JSON.stringify(data), init);
};

// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
const getIsSSR = () => Boolean(process.env.REACT_SSR);

const POST_OFFICE_SERVICE_NAME = 'post-office';
const POST_OFFICE_ARCHETYPE_NAME = 'post-office-archetype';

type PlacementDataWriter = (url: string, data: unknown) => void;

const confluenceFetcher = async (
	url: string,
	headers?: Record<string, string>,
	requestInit?: RequestInit,
	writeData: PlacementDataWriter = (url, data) => postOfficeClient.setDataWithUrl(url, data),
) => {
	// When in SPA mode, we want to re-use the pending fetcher if it exists - so that we don't make another Network request from the Transition preloaders
	if (!getIsSSR()) {
		const cachedFetcher = postOfficeClient.getFetcherWithUrl(url);
		if (cachedFetcher) {
			return cachedFetcher;
		}
	}

	let fetcher;

	if (process.env.BIFROST_TEMPLATE_BUILD === 'true') {
		fetcher = cfetch(url, { ...requestInit, headers });
	} else {
		fetcher = defaultFetcher(url, headers, requestInit);
	}

	fetcher = fetcher
		.then(assertResponseIsOk)
		.then((res) => res.json())
		.then((data) => {
			writeData(url, data);
			return wrapResolvedDataAsFetchResponse(data);
		});

	// When we're in SPA mode, we want to cache the fetcher so that it can be reused
	// if the network request takes too long and the data doesn't come back in time
	// before another request is requested by <Placement>
	if (!getIsSSR()) {
		postOfficeClient.setFetcherWithUrl(url, fetcher);
	}

	return fetcher;
};

type CookieKeys = 'consentToken' | 'fallbackConsentToken';

const cookieMap = new Map<CookieKeys, string>([
	['consentToken', ATLASSIAN_CONSENT_COOKIE_KEY],
	['fallbackConsentToken', ATLASSIAN_CONSENT_FALLBACK_COOKIE_KEY],
]);

const mapCookiesToFetchHeaders = (cookies: Record<CookieKeys, string | undefined>) => {
	if (typeof cookies !== 'object' || cookies === null) {
		return undefined;
	}

	return Array.from(cookieMap.entries())
		.map(([key, value]) => {
			if (cookies[key]) {
				return `${value}=${cookies[key]}`;
			}

			return '';
		})
		.filter(Boolean)
		.join(';');
};

/**
 * Common method for setting up the Post Office placement request URL with the environment and product context
 *
 * @param placementId
 * @param contextOptions
 * @returns
 */
const preloadPostOfficePlacement = async (
	placementId: string,
	contextOptions: {
		postOfficeContext: PostOfficeContextValue;
		environment: OptionalEnvConfigsForPostOfficeEnvironment;
	},
	extraFetchOptions?: {
		resource?: string;
		requestInit?: RequestInit;
	},
	writeData?: PlacementDataWriter,
) => {
	const { postOfficeContext, environment } = contextOptions;

	// Mirror client-side guard in Placement.tsx (useInitialDataIfEnabled → isPostOfficePlacementsGloballyDisabled)
	// to prevent SSR/client mismatch when placements are globally disabled (e.g. stableUI=1).
	if (postOfficeContext.disableAllPlacements) {
		return;
	}

	const confluenceFetcherWithS2SHeaders = async (url: string) => {
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		const isSSR = Boolean(process.env.REACT_SSR);
		if (isSSR) {
			let hostEnv = 'prod';
			if (environment.currentEnv !== 'production') {
				hostEnv = 'staging';
			}

			const { cookiesConsentToken, cookiesFallbackToken, isBifrostIcRequest } =
				getSSRRenderInputs();

			const cookies = mapCookiesToFetchHeaders({
				consentToken: cookiesConsentToken,
				fallbackConsentToken: cookiesFallbackToken,
			});

			const fetchHeaders: Record<string, string> = !isLocal()
				? {
						'x-ssr-service-name': POST_OFFICE_SERVICE_NAME, // Tells Tesseract to S2S with `post-office`
						...(isBifrostIcRequest === true
							? { 'x-archetype-name': POST_OFFICE_ARCHETYPE_NAME }
							: {}),
						'x-ssr-service-env': hostEnv, // Defaults to current Env, however Post Office is not in dev
					}
				: {};

			if (cookies) {
				fetchHeaders.Cookie = cookies;
			}

			const finalUrl = isLocal() ? url : url.replace('gateway/api/post-office/', '');

			return confluenceFetcher(finalUrl, fetchHeaders, extraFetchOptions?.requestInit, writeData);
		}

		return confluenceFetcher(url, undefined, extraFetchOptions?.requestInit, writeData);
	};

	const basePath = buildBasePath(environment.currentEnv, environment.envConfigs);

	return requestInitialData(placementId, postOfficeContext, confluenceFetcherWithS2SHeaders, {
		...basePath,
		resource: extraFetchOptions?.resource,
	});
};

/**
 * Returns a fetcher for Post Office placement API that is shared with the SSR preload
 *
 * @param placementId
 * @returns
 */
export const usePlacementSSRFetch = (): ((url: string, requestOptions?: RequestInit) => any) => {
	return useCallback((url: string, requestOptions?: RequestInit) => {
		// Use data in cache if it exists
		const data = postOfficeClient.getDataWithUrl(url);
		if (data) {
			/**
			 * Preloaded data must be serialized, however our initialData is expected to be in the form of a Response promise.
			 * TODO: Remove this workaround once the Post Office placement API is updated to handle flat data.
			 */
			return Promise.resolve(wrapResolvedDataAsFetchResponse(data));
		}

		// When in SPA mode, we want to re-use the pending fetcher if it exists - so that we don't make another Network request from the Placement component
		if (!getIsSSR()) {
			const fetcher = postOfficeClient.getFetcherWithUrl(url);
			if (fetcher) {
				return fetcher;
			}
		}

		// Convert headers to Record<string, string> if present
		let headers: Record<string, string> = {};
		if (requestOptions?.headers) {
			if (requestOptions.headers instanceof Headers) {
				requestOptions.headers.forEach((value: string, name: string) => {
					headers[name] = value;
				});
			} else if (Array.isArray(requestOptions.headers)) {
				requestOptions.headers.forEach(([name, value]) => {
					headers[name] = value;
				});
			} else {
				headers = requestOptions.headers;
			}
		}

		// Use a new fetcher if neither data nor fetcher is in cache
		return confluenceFetcher(url, headers, requestOptions);
	}, []);
};

/**
 * Returns the Post Office placement initial data from the cache if it exists
 *
 * @param placementId
 * @returns Post Office placement initial data from cache
 */
export const usePlacementPreloadedData = (
	placementId: string,
): [unknown, (data: unknown) => void] => {
	const [preloadedData, setPreloadedData] = useState<unknown>(() => {
		const data = postOfficeClient.getDataWithPlacementId(placementId);
		if (data) {
			return data;
		}

		return undefined;
	});

	const setter = useCallback(
		(data: unknown) => {
			postOfficeClient.setDataWithPlacementId(placementId, data);
			setPreloadedData(data);
		},
		[placementId],
	);

	return [preloadedData, setter];
};

/**
 * Preload a Post Office placement API via SSR
 *
 * @param placementId
 * @param preloadedSessionContext
 * @returns
 */
export const preloadPostOfficePlacementWithSessionContext = async (
	placementId: string,
	extraFetchOptions?: {
		resource?: string;
		requestInit?: RequestInit;
	},
): Promise<boolean> => {
	const sessionContext = getPreloaderFnContext();
	const postOfficeTimeout = StatsigConfigurations.getNumber(
		'confluence_frontend_preload_postoffice_timeout',
		SLOW_QUERY_DEFAULT_TIMEOUT,
	);
	const preloadPlacement = (writeData?: PlacementDataWriter) =>
		slowQueryGuard(
			`preloadPostOfficePlacement:${placementId}`,
			() =>
				preloadPostOfficePlacement(
					placementId,
					{
						postOfficeContext: sessionToPostOfficeContext(sessionContext),
						environment: {
							currentEnv: transformConfluenceEnvToPostOfficeEnvironment(sessionContext.environment),
						},
					},
					extraFetchOptions,
					writeData,
				),
			postOfficeTimeout,
		);

	if (
		getIsSSR() &&
		window.__SSR_REACT_STREAMING__ &&
		expValEquals('confluence_react_streaming_all_routes', 'isEnabled', true)
	) {
		// Publish only data from a preload that finished before its timeout. A late response
		// must not change the snapshot after SSR has rendered the empty placement.
		const pendingData = new Map<string, unknown>();
		const completed = await preloadPlacement((url, data) => pendingData.set(url, data));
		if (completed) {
			pendingData.forEach((data, url) => postOfficeClient.setDataWithUrl(url, data));
		}
		return completed;
	}

	return preloadPlacement();
};
